import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { defaultTo, get, isEmpty } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { FeatureFlags } from './constants';

export type FeatureFlagsMap = Map<string, boolean>;

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService {
  private http = inject(HttpClient);

  // holds the list of feature flags
  private featureFlagList = new Map<string, boolean>();
  private initialSet = new Map<string, boolean>();
  private _featureFlagsMap$ = new BehaviorSubject<FeatureFlagsMap>(
    this.initialSet
  );

  getAllFeatureFlags(): void {
    this.featureFlagList.clear(); // reset the map
    const featureFlagNames = this.getFeatureFlagValues();
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'en-namespace': environment.namespace,
      }),
    };
    const data = {
      featureFlagNames,
    };
    const url = environment.externalServiceUrl + 'fetchFeatureFlags';
    this.http
      .post(url, data, httpOptions)
      .pipe(take(1))
      .subscribe((result) => {
        const featureFlags = defaultTo(get(result, 'featureFlags'), {});
        if (!isEmpty(featureFlags)) {
          featureFlagNames.forEach((featureFlagName) => {
            const value: boolean = defaultTo(
              get(featureFlags, featureFlagName),
              false
            );
            this.featureFlagList.set(featureFlagName, value);
            this._featureFlagsMap$.next(this.featureFlagList);
          });
        }
      });
  }

  getFeatureFlagValues(): string[] {
    return Object.values(FeatureFlags);
  }

  getFeatureFlag(ffName: string): boolean {
    return this.featureFlagList.get(ffName);
  }

  isFeatureEnabled$(
    featureFlagName: string,
    defaultIfFeatureMissing: boolean = false
  ): Observable<boolean> {
    return this._featureFlagsMap$.pipe(
      map((flags) => {
        return flags.has(featureFlagName)
          ? flags.get(featureFlagName)
          : defaultIfFeatureMissing;
      })
    );
  }
}
